'use client';

import { SelectedProductOption } from '@/app/sales/components/bottomSheet/physical/types';
import { createContext, ReactNode, useContext, useRef, useState } from 'react';

// 카트 타입
interface CartContextValue {
  cartItems: SelectedProductOption[];
  setCartItems: (
    items:
      | SelectedProductOption[]
      | ((prev: SelectedProductOption[]) => SelectedProductOption[])
  ) => void;
  cartItemsRef: React.MutableRefObject<SelectedProductOption[]>;
  selectedId: number | null;
  setSelectedId: (id: number | null) => void;
  selectedProductIds: number[];
  setSelectedProductIds: (
    ids: number[] | ((prev: number[]) => number[])
  ) => void;
}

const CartContext = createContext<CartContextValue | undefined>(undefined);

// provider 타입
interface CartProviderProps {
  children: ReactNode;
}

export const CartProvider = ({ children }: CartProviderProps) => {
  const [cartItems, setCartItems] = useState<SelectedProductOption[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [selectedProductIds, setSelectedProductIds] = useState<number[]>([]);
  const cartItemsRef = useRef<SelectedProductOption[]>([]);

  const handleSetCartItems = (
    items:
      | SelectedProductOption[]
      | ((prev: SelectedProductOption[]) => SelectedProductOption[])
  ) => {
    const newItems =
      typeof items === 'function' ? items(cartItemsRef.current) : items;

    cartItemsRef.current = newItems;
    setCartItems(newItems);
  };

  const value: CartContextValue = {
    cartItems,
    setCartItems: handleSetCartItems,
    cartItemsRef,
    selectedId,
    setSelectedId,
    selectedProductIds,
    setSelectedProductIds,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
